/**
 * The capital register, as a file somebody can open in a spreadsheet.
 *
 * Every row that leaves this platform has to survive being read by somebody
 * who cannot ask it a question, and that is where a register like this one
 * usually loses its meaning. Three things are handled deliberately.
 *
 * **Money the register does not know stays unknown.** A project's money is
 * `declared` or `not_declared`; the platform is never told the difference
 * between "nothing has been committed" and "nobody has said". Writing 0 for
 * the second turns an admission into a figure, and the figure then gets
 * summed. Undeclared money leaves an empty cell, and a column beside it says
 * which it was.
 *
 * **Minor units become major ones once, here.** The platform holds money in
 * the currency's minor unit, which is what a ledger should do and not what a
 * spreadsheet expects. Halalas exported as riyals are a hundredfold error
 * that looks entirely plausible.
 *
 * **The platform's own sentence about the schedule is carried, not
 * recomputed.** `schedule.why` is the platform's wording for why a project
 * is where it is; this file quotes it rather than deriving a second opinion
 * that could disagree with the screen it was exported from.
 */
import type { ProjectRow } from "./projects";
import { PROJECT_STATUS_LABEL, SCHEDULE_LABEL } from "./projects";

/** One heading per column, in the order they are written. */
export const PROJECT_CSV_COLUMNS = [
  "Code",
  "Name",
  "Status",
  "Category",
  "Contractor",
  "Schedule",
  "Why",
  "Baseline start",
  "Baseline end",
  "Current start",
  "Current end",
  "End slip (days)",
  "Overdue (days)",
  "Times rebaselined",
  "Currency",
  "Budget",
  "Committed",
  "Spent",
  "Money declared?",
  "Gates approved",
  "Gates total",
  "Hold points unreleased",
  "Work stopped?",
] as const;

/** A cell that has to survive Excel: quotes doubled, the whole thing quoted. */
function cell(v: string | number | undefined | null): string {
  if (v === undefined || v === null || v === "") return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.split('"').join('""')}"` : s;
}

/** Minor units to major, as a plain decimal — never a locale-formatted string. */
function money(minor: number | undefined, declared: boolean): string {
  if (!declared || typeof minor !== "number" || !Number.isFinite(minor)) return "";
  return (minor / 100).toFixed(2);
}

export function projectRowCells(p: ProjectRow): string[] {
  const declared = p.money.state === "declared";
  const sch = p.schedule;
  return [
    p.code,
    p.name,
    PROJECT_STATUS_LABEL[p.status] ?? p.status,
    p.category ?? "",
    p.contractor ?? "",
    SCHEDULE_LABEL[sch.state] ?? sch.state,
    sch.why ?? "",
    sch.baselineStart ?? "",
    sch.baselineEnd ?? "",
    sch.startDate ?? "",
    sch.endDate ?? "",
    sch.endSlipDays ?? "",
    sch.overdueDays ?? "",
    sch.rebaselined,
    declared ? (p.money.currency ?? "") : "",
    money(p.money.budgetMinor, declared),
    money(p.money.committedMinor, declared),
    money(p.money.spentMinor, declared),
    declared ? "declared" : "not declared",
    p.gatesApproved,
    p.gates,
    p.holding,
    p.workStopped ? "yes" : "no",
  ].map((v) => cell(v as string | number));
}

/**
 * The whole register as CSV.
 *
 * CRLF between rows, because that is what the format says and what Excel on
 * Windows expects; the byte-order mark is added by the saver, not here, so
 * this stays a pure string a test can compare.
 */
export function projectsCsv(rows: ProjectRow[]): string {
  const lines = [PROJECT_CSV_COLUMNS.map((h) => cell(h)).join(",")];
  for (const p of rows) lines.push(projectRowCells(p).join(","));
  return lines.join("\r\n") + "\r\n";
}
